import { truncateText } from "@/truncateText/truncateText.js";
import { getHashObj } from "@/utils/utils";

export default class TextShowMoreLess {
	#rootDiv;
	#maxLines;
	#textSelector;
	#showMoreSelector;
	#showLessSelector;
	#expanded;
	#customShowMoreFunction;

	#textDiv;
	#readMoreBtn;
	#readLessBtn;
	#disableButtons;
	#textDivOriginalHtml;

	constructor(
		rootDiv,
		maxLines = 4,
		textSelector = ".show-more-less-text",
		showMoreSelector = ".read-more",
		showLessSelector = ".read-less",
		customShowMoreFunction = undefined,
		expanded = false,
		disableButtons = false,
		textDivOriginalHtml = undefined
	) {
		this.#rootDiv = rootDiv;
		this.#maxLines = maxLines;
		this.#textSelector = textSelector;
		this.#showMoreSelector = showMoreSelector;
		this.#showLessSelector = showLessSelector;
		this.#expanded = expanded;
		this.#customShowMoreFunction = customShowMoreFunction;
		this.#disableButtons = disableButtons;
		this.#textDivOriginalHtml = textDivOriginalHtml;
	}

	// getters

	get rootDiv() {
		return this.#rootDiv;
	}
	get maxLines() {
		return this.#maxLines;
	}
	get textSelector() {
		return this.#textSelector;
	}
	get showMoreSelector() {
		return this.#showMoreSelector;
	}
	get showLessSelector() {
		return this.#showLessSelector;
	}
	get expanded() {
		return this.#expanded;
	}
	get customShowMoreFunction() {
		return this.#customShowMoreFunction;
	}

	get textDivOriginalHtml() {
		return this.#textDivOriginalHtml;
	}

	// setters

	set rootDiv(rootDiv) {
		this.#rootDiv = rootDiv;
	}
	set maxLines(maxLines) {
		this.#maxLines = maxLines;
	}
	set textSelector(textSelector) {
		this.#textSelector = textSelector;
	}
	set showMoreSelector(showMoreSelector) {
		this.#showMoreSelector = showMoreSelector;
	}
	set showLessSelector(showLessSelector) {
		this.#showLessSelector = showLessSelector;
	}
	set expanded(expanded) {
		this.#expanded = expanded;
	}
	set customShowMoreFunction(customShowMoreFunction) {
		this.#customShowMoreFunction = customShowMoreFunction;
	}

	set textDivOriginalHtml(textDivOriginalHtml) {
		this.#textDivOriginalHtml = textDivOriginalHtml;
	}

	// private methods

	#getLineHeight() {
		const style = window.getComputedStyle(this.#textDiv);
		return parseInt(style["lineHeight"]);
	}

	#getRequiredMaxHeight() {
		return parseInt(this.#maxLines * this.#getLineHeight());
	}

	#setListeners() {
		if (this.#readMoreBtn && !this.#disableButtons)
			this.#readMoreBtn.addEventListener("click", () => {
				if (this.#customShowMoreFunction) {
					this.#customShowMoreFunction();
				} else {
					this.expand();
				}
			});

		if (this.#readLessBtn && !this.#disableButtons)
			this.#readLessBtn.addEventListener("click", () => {
				this.collapse();
			});
	}

	#displayButton(type) {
		if (type === "more") {
			if (this.#readMoreBtn) this.#readMoreBtn.style.display = "flex";
			if (this.#readLessBtn) this.#readLessBtn.style.display = "none";
		} else if (type === "less") {
			if (this.#readMoreBtn) this.#readMoreBtn.style.display = "none";
			if (this.#readLessBtn) this.#readLessBtn.style.display = "flex";
		}
	}

	#setInitialStyles() {
		// buttons
		if (this.#readMoreBtn) this.#readMoreBtn.style.display = "flex";
		if (this.#readLessBtn) this.#readLessBtn.style.display = "none";

		// rootDiv
		this.#textDiv.style.overflowY = "hidden";
	}

	#adjustStyles() {
		const requiredHeight = this.#getRequiredMaxHeight();
		const actualHeight = this.#rootDiv.querySelector(
			this.#textSelector
		).clientHeight;

		if (requiredHeight > actualHeight - 10) {
			if (this.#readMoreBtn) this.#readMoreBtn.style.display = "none";
			if (this.#readLessBtn) this.#readLessBtn.style.display = "none";
			this.#textDiv.style.maxHeight = "none";
			return;
		}

		this.#textDiv.style.maxHeight = `${requiredHeight}px`;
		this.#textDivOriginalHtml = this.#textDiv.innerHTML;
	}

	// public methods

	init() {
		setTimeout(() => {
			if (this.#rootDiv.getAttribute("data-textshowmoreless-applied")) {
				return;
			}

			this.#textDiv = this.#rootDiv.querySelector(this.#textSelector);
			this.#readMoreBtn = this.#rootDiv.querySelector(this.#showMoreSelector);
			this.#readLessBtn = this.#rootDiv.querySelector(this.#showLessSelector);
			this.#setListeners();
			this.#setInitialStyles();
			this.#adjustStyles();
			this.#rootDiv.setAttribute("data-textshowmoreless-applied", true);
			// store the original text of the text div, to later be used in the truncate function
			this.#textDivOriginalHtml = this.#textDiv.innerHTML;
			truncateText(this.#textDiv, this.#maxLines, this.#textDivOriginalHtml);
		}, 0);

		window.addEventListener(
			"hashchange",
			() => {
				const { season: newSeason } = getHashObj(window.location.hash);
				if (newSeason !== window.seasonManager.originalSeason) {
					this.#textDivOriginalHtml = this.#textDiv.innerHTML.replace(
						"...",
						""
					);
					truncateText(
						this.#textDiv,
						this.#maxLines,
						this.#textDivOriginalHtml
					);
				}
			},
			true
		);
	}

	expand() {
		this.#expanded = true;
		this.#textDiv.style.overflowY = "visible";
		this.#textDiv.style.maxHeight = "none";
		this.#displayButton("less");
		// store the original text of the text div, to later be used in the truncate function
		this.#textDiv.innerHTML = this.#textDivOriginalHtml;
	}

	collapse() {
		this.#expanded = false;
		this.#adjustStyles();
		this.#textDiv.style.overflowY = "hidden";
		this.#displayButton("more");
		truncateText(this.#textDiv, this.#maxLines, this.#textDivOriginalHtml);
	}

	setMaxLines(num) {
		this.#maxLines = num;
		if (!this.#expanded) {
			this.#adjustStyles();
		}
	}
}
